import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  Eye, Users, TrendingUp, MessageSquare, Home, MapPin, FileText,
  Star, Loader2, ShieldCheck, Mail, Phone,
  ImageOff, DollarSign, Clock, Send, ArrowUpRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAnalytics = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/analytics?days=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-40">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  const o = data.overview;
  const health = data.listingHealth;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-analytics">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-foreground mb-1">Analytics Dashboard</h1>
          <p className="text-sm text-muted-foreground">Real-time insights from your platform data</p>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard icon={Home} label="Homestays" value={o.totalHomestays} sub={`${o.publishedHomestays} live`} color="bg-primary" />
          <StatCard icon={Eye} label="Total Views" value={o.totalViews} sub="All time" color="bg-blue-500" />
          <StatCard icon={Phone} label="Call Clicks" value={o.totalCallClicks || 0} sub="All time" color="bg-green-600" />
          <StatCard icon={MessageSquare} label="Inquiries" value={o.totalInquiries} sub={`${data.inquiryBreakdown?.pending || 0} pending`} color="bg-amber-500" />
          <StatCard icon={Star} label="Reviews" value={o.totalReviews} sub={`${o.pendingReviews} pending`} color="bg-yellow-500" />
          <StatCard icon={MapPin} label="Locations" value={o.totalLocations} color="bg-teal-500" />
          <StatCard icon={FileText} label="Blog Posts" value={o.totalBlogPosts} color="bg-purple-500" />
        </div>

        {/* Most Viewed Homestays */}
        <div className="bg-white rounded-2xl p-6 shadow-soft" data-testid="most-viewed-section">
          <div className="flex items-center gap-2 mb-4">
            <Eye className="w-5 h-5 text-blue-500" />
            <h2 className="text-lg font-semibold text-foreground">Most Viewed Homestays</h2>
          </div>
          {data.mostViewed.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-muted-foreground text-left">
                    <th className="pb-2 font-medium">#</th>
                    <th className="pb-2 font-medium">Homestay</th>
                    <th className="pb-2 font-medium">Location</th>
                    <th className="pb-2 font-medium text-right">Views</th>
                    <th className="pb-2 font-medium text-right">Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {data.mostViewed.map((h, i) => (
                    <tr key={h.id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="py-3 text-muted-foreground">{i + 1}</td>
                      <td className="py-3 font-medium">{h.name}</td>
                      <td className="py-3 text-muted-foreground">{h.location_name || "-"}</td>
                      <td className="py-3 text-right font-semibold text-blue-600">{h.view_count}</td>
                      <td className="py-3 text-right">
                        {h.average_rating > 0 ? (
                          <span className="inline-flex items-center gap-1">
                            <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                            {h.average_rating.toFixed(1)}
                          </span>
                        ) : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">No view data yet. Views are tracked when guests visit homestay pages.</p>
          )}
        </div>

        {/* Call Click Analytics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Most Called Homestays */}
          <div className="bg-white rounded-2xl p-6 shadow-soft" data-testid="most-called-section">
            <div className="flex items-center gap-2 mb-4">
              <Phone className="w-5 h-5 text-green-600" />
              <h2 className="text-lg font-semibold text-foreground">Most Called Homestays</h2>
            </div>
            {data.mostCalled?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-muted-foreground text-left">
                      <th className="pb-2 font-medium">#</th>
                      <th className="pb-2 font-medium">Homestay</th>
                      <th className="pb-2 font-medium">Location</th>
                      <th className="pb-2 font-medium text-right">Calls</th>
                      <th className="pb-2 font-medium text-right">Views</th>
                      <th className="pb-2 font-medium text-right">CTR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.mostCalled.map((h, i) => {
                      const ctr = h.view_count > 0 ? ((h.call_clicks / h.view_count) * 100).toFixed(1) : "—";
                      return (
                        <tr key={h.id} className="border-b last:border-0 hover:bg-muted/30">
                          <td className="py-3 text-muted-foreground">{i + 1}</td>
                          <td className="py-3 font-medium">{h.name}</td>
                          <td className="py-3 text-muted-foreground">{h.location_name || "—"}</td>
                          <td className="py-3 text-right font-semibold text-green-600">{h.call_clicks}</td>
                          <td className="py-3 text-right text-muted-foreground">{h.view_count || 0}</td>
                          <td className="py-3 text-right text-xs font-medium text-amber-600">{ctr}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">No call clicks recorded yet. Tracking starts when guests tap the call button.</p>
            )}
          </div>

          {/* Recent Call Activity */}
          <div className="bg-white rounded-2xl p-6 shadow-soft" data-testid="recent-calls-section">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-amber-500" />
              <h2 className="text-lg font-semibold text-foreground">Recent Call Clicks</h2>
            </div>
            {data.recentCalls?.length > 0 ? (
              <div className="space-y-3">
                {data.recentCalls.map((c, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 p-3 bg-muted/30 rounded-xl">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-4 h-4 text-green-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{c.homestay_name}</p>
                        <p className="text-xs text-muted-foreground">{c.location_name || "—"}</p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground flex-shrink-0">
                      {new Date(c.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">No recent call activity. Call tracking is active on all homestay pages.</p>
            )}
          </div>
        </div>

        {/* Traffic Sources + Homestays by Location */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Traffic Sources */}
          <div className="bg-white rounded-2xl p-6 shadow-soft" data-testid="traffic-sources">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
              <h2 className="text-lg font-semibold">Traffic Sources</h2>
              {o.totalPageViews > 0 && (
                <span className="text-xs text-muted-foreground ml-auto">{o.totalPageViews} visits (last 30 days)</span>
              )}
            </div>
            {(data.trafficSources || []).length > 0 ? (
              <div className="space-y-3">
                {data.trafficSources.map((src) => {
                  const totalPV = o.totalPageViews || 1;
                  const pct = Math.round((src.count / totalPV) * 100);
                  const colors = {
                    "Google": "bg-blue-500",
                    "Direct": "bg-slate-500",
                    "Facebook": "bg-blue-600",
                    "Instagram": "bg-pink-500",
                    "WhatsApp": "bg-green-500",
                    "Twitter/X": "bg-gray-800",
                    "YouTube": "bg-red-500",
                    "LinkedIn": "bg-blue-700",
                    "Bing": "bg-teal-500",
                    "Referral": "bg-amber-500",
                  };
                  const barColor = colors[src.source] || "bg-indigo-400";
                  return (
                    <div key={src.source}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium">{src.source}</span>
                        <span className="text-muted-foreground">{src.count} ({pct}%)</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">No traffic data yet. Visits are tracked when guests browse your site.</p>
            )}
          </div>

          {/* Homestays by Location */}
          <div className="bg-white rounded-2xl p-6 shadow-soft" data-testid="location-distribution">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-teal-500" />
              <h2 className="text-lg font-semibold">Homestays by Location</h2>
            </div>
            {data.homestaysByLocation.length > 0 ? (
              <div className="space-y-3">
                {data.homestaysByLocation.map((loc) => {
                  const pct = Math.round((loc.count / o.totalHomestays) * 100);
                  return (
                    <div key={loc.location}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium">{loc.location}</span>
                        <span className="text-muted-foreground">{loc.count} ({pct}%)</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">No location data available.</p>
            )}
          </div>
        </div>

        {/* Listing Health */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl p-6 shadow-soft" data-testid="listing-health">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="w-5 h-5 text-green-500" />
              <h2 className="text-lg font-semibold">Listing Health</h2>
            </div>
            <div className="space-y-3">
              <HealthBar label="Published" value={o.publishedHomestays} total={health.total} color="bg-green-500" icon={ShieldCheck} />
              <HealthBar label="With Images" value={health.withImages} total={health.total} color="bg-blue-500" icon={Eye} />
              <HealthBar label="With Price" value={health.withPrice} total={health.total} color="bg-amber-500" icon={DollarSign} />
              <HealthBar label="With Description" value={health.withDescription} total={health.total} color="bg-purple-500" icon={FileText} />
              <HealthBar label="With Owner Email" value={health.withOwnerEmail} total={health.total} color="bg-teal-500" icon={Mail} />
            </div>
            <div className="mt-4 pt-3 border-t flex items-center gap-2 text-xs text-muted-foreground">
              {health.withoutImages > 0 && (
                <span className="flex items-center gap-1 bg-red-50 text-red-600 px-2 py-1 rounded-full">
                  <ImageOff className="w-3 h-3" /> {health.withoutImages} missing images
                </span>
              )}
              {health.withoutPrice > 0 && (
                <span className="flex items-center gap-1 bg-amber-50 text-amber-600 px-2 py-1 rounded-full">
                  <DollarSign className="w-3 h-3" /> {health.withoutPrice} missing price
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Recent Inquiries */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl p-6 shadow-soft" data-testid="recent-inquiries">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-slate-500" />
              <h2 className="text-lg font-semibold">Recent Inquiries</h2>
            </div>
            {data.recentInquiries.length > 0 ? (
              <div className="space-y-3">
                {data.recentInquiries.map((inq) => (
                  <div key={inq.id} className="flex items-center justify-between gap-2 py-2 border-b last:border-0">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{inq.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{inq.homestay_name}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <StatusBadge status={inq.status} />
                      {inq.forwarded_status === "sent" && (
                        <span className="text-xs text-green-600"><Send className="w-3 h-3" /></span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">No inquiries yet.</p>
            )}
          </div>
        </div>

        {/* Recently Added Homestays */}
        <div className="bg-white rounded-2xl p-6 shadow-soft" data-testid="recent-homestays">
          <div className="flex items-center gap-2 mb-4">
            <ArrowUpRight className="w-5 h-5 text-green-500" />
            <h2 className="text-lg font-semibold">Recently Added Homestays</h2>
          </div>
          {data.recentHomestays.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {data.recentHomestays.map((h) => (
                <div key={h.id} className="border rounded-xl p-3">
                  <p className="text-sm font-medium truncate">{h.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{h.location_name || "No location"}</p>
                  <p className="text-xs text-muted-foreground mt-1">{h.created_at?.slice(0, 10)}</p>
                  {h.is_published === false && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded mt-1 inline-block">Draft</span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">No homestays added yet.</p>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

/* Helper components */
const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="bg-white rounded-xl p-4 shadow-soft">
    <div className={`w-9 h-9 ${color} rounded-lg flex items-center justify-center mb-2`}>
      <Icon className="w-4 h-4 text-white" />
    </div>
    <p className="text-2xl font-bold text-foreground">{value}</p>
    <p className="text-xs text-muted-foreground">{label}</p>
    {sub && <p className="text-xs text-muted-foreground/70 mt-0.5">{sub}</p>}
  </div>
);

const HealthBar = ({ label, value, total, color, icon: Icon }) => {
  const pct = Math.round((value / (total || 1)) * 100);
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5 text-muted-foreground" />
          {label}
        </span>
        <span className="text-muted-foreground">{value}/{total} ({pct}%)</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const styles = {
    pending: "bg-yellow-100 text-yellow-700",
    contacted: "bg-blue-100 text-blue-700",
    confirmed: "bg-green-100 text-green-700",
    cancelled: "bg-red-100 text-red-700",
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${styles[status] || "bg-gray-100 text-gray-700"}`}>
      {status}
    </span>
  );
};

export default AdminAnalytics;
